import { useEffect, useRef, useState } from 'react';
import type { Map as LibreMap, Marker } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?url';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { RegionalCell, RegionalOverview } from '../../consumer/regional-monitoring';
import { basemap, MAP_PROVIDER, mapStyleUrl, mapFailure } from '../../consumer/geospatial/config';
import {
  createMapLoadAttempt,
  isLoadedProviderTile,
  type MapLoadState,
} from '../../consumer/geospatial/load-attempt';

export function RegionalMap({
  cells,
  bounds,
  selected,
  select,
}: {
  cells: RegionalCell[];
  bounds: RegionalOverview['region']['bounds'];
  selected: string;
  select: (id: string) => void;
}) {
  const host = useRef<HTMLDivElement>(null),
    mapRef = useRef<LibreMap | null>(null),
    markers = useRef<Marker[]>([]);
  const latest = useRef({ cells, selected, select });
  latest.current = { cells, selected, select };
  const [state, setState] = useState<MapLoadState>('MAP_LOADING'),
    [attempt, setAttempt] = useState(0);
  const key = String(import.meta.env.VITE_MAPTILER_API_KEY ?? '').trim();
  const validKey = /^[A-Za-z0-9_-]{8,200}$/.test(key);
  useEffect(() => {
    if (!validKey || !host.current) return;
    let disposed = false,
      map: LibreMap | null = null,
      resize: ResizeObserver | null = null;
    const load = createMapLoadAttempt(setState),
      token = load.begin();
    let hasTile = false;
    void import('maplibre-gl')
      .then((lib) => {
        if (disposed || !host.current) return;
        lib.setWorkerUrl(workerUrl);
        try {
          map = new lib.Map({
            container: host.current,
            style: mapStyleUrl(basemap(import.meta.env.VITE_MAPTILER_DEFAULT_STYLE), key),
            center: [(bounds.west + bounds.east) / 2, (bounds.south + bounds.north) / 2],
            zoom: 10,
            attributionControl: { compact: false },
            transformRequest: (url) => {
              const target = new URL(url);
              if (
                target.protocol !== 'https:' ||
                !MAP_PROVIDER.allowedHosts.some((h) => h === target.hostname)
              )
                throw new Error('MAP_RESOURCE_ORIGIN_REJECTED');
              return { url };
            },
          });
          mapRef.current = map;
          map.fitBounds(
            [
              [bounds.west, bounds.south],
              [bounds.east, bounds.north],
            ],
            { padding: 50, duration: 0 },
          );
          const active = map;
          active.addControl(new lib.NavigationControl({ showCompass: false }), 'top-right');
          active.on('error', (event) => {
            const err = event.error as Error & { status?: number };
            load.fail(token, mapFailure(err.status ?? 0, !active.isStyleLoaded()));
          });
          active.on('sourcedata', (event) => {
            if (isLoadedProviderTile(event)) hasTile = true;
          });
          active.on('idle', () =>
            load.complete(token, active.isStyleLoaded() === true, active.areTilesLoaded(), hasTile),
          );
          active.on('webglcontextlost', () => load.fail(token, 'WEBGL_UNAVAILABLE'));
          resize = new ResizeObserver(() => active.resize());
          resize.observe(host.current);
        } catch {
          load.fail(token, 'WEBGL_UNAVAILABLE');
        }
      })
      .catch(() => {
        if (!disposed) load.fail(token, 'MAP_OFFLINE');
      });
    return () => {
      disposed = true;
      load.dispose();
      resize?.disconnect();
      markers.current.forEach((m) => m.remove());
      markers.current = [];
      map?.remove();
      mapRef.current = null;
    };
  }, [key, validKey, bounds.west, bounds.south, bounds.east, bounds.north, attempt]);
  useEffect(() => {
    if (state !== 'MAP_BASEMAP_REAL' || !mapRef.current) return;
    let disposed = false;
    void import('maplibre-gl').then((lib) => {
      if (disposed || !mapRef.current) return;
      markers.current.forEach((m) => m.remove());
      markers.current = cells.map((cell) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `regional-map-point${cell.id === selected ? ' is-selected' : ''}${cell.hitCount ? ' has-hit' : ''}${cell.status === 'UNAVAILABLE' ? ' is-unknown' : ''}`;
        button.textContent = cell.requestedPoints[0]?.id.toUpperCase() ?? '点';
        button.setAttribute(
          'aria-label',
          `${button.textContent} ${cell.latitude.toFixed(4)}北纬 ${cell.longitude.toFixed(4)}东经，${cell.status === 'UNAVAILABLE' ? '数据不可用' : cell.hitCount ? '有阈值命中' : '查看预报'}`,
        );
        button.setAttribute('aria-pressed', String(cell.id === selected));
        button.addEventListener('click', () => latest.current.select(cell.id));
        return new lib.Marker({ element: button })
          .setLngLat([cell.longitude, cell.latitude])
          .addTo(mapRef.current!);
      });
    });
    return () => {
      disposed = true;
    };
  }, [cells, selected, state]);
  const unavailable = !validKey || (state !== 'MAP_LOADING' && state !== 'MAP_BASEMAP_REAL');
  return (
    <div className="regional-map-wrap">
      <div className="regional-map" ref={host} aria-label="杭州示例区域采样地图" />
      {state !== 'MAP_BASEMAP_REAL' && (
        <div className="regional-map-message" role="status">
          <strong>
            {!validKey ? '地图底图未配置' : unavailable ? '地图暂不可用' : '正在载入区域地图'}
          </strong>
          <span>
            {unavailable
              ? '使用下方采样点列表，仍可查看数据并发起核查。'
              : '供应方真实格点将在底图加载后显示。'}
          </span>
          {validKey && unavailable && (
            <button
              type="button"
              className="regional-button"
              onClick={() => setAttempt((n) => n + 1)}
            >
              重试地图
            </button>
          )}
        </div>
      )}
      <div className="regional-map-caption">WGS84 · 示例矩形，非行政边界 · 点位表示预报格点</div>
    </div>
  );
}
